import { ReactNode, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  setReferralProject,
  setReferralProjectData,
} from '../../modules/miscUi/actions'
import { selectReferralProject } from '../../modules/miscUi/selectors'
import EmbeddableModal, {
  ModalTitle,
  ModalText,
} from '../../shared_components/EmbeddableModal'
import { ThemedButton } from '../../shared_components/ThemedButton'
import { Box } from '../../shared_components/UI/Box'
import useWeb3 from '../../hooks/useWeb3'
import useIsWalletConnected from '../../shared_components/Header/useIsWalletConnected'
import {
  ReferralVerification,
  ReferralVerificationUnion,
} from '../../shared_components/Missions/types'
import { isNotNil } from 'ramda'

const ReferralWelcomeModal = () => {
  const isConnected = useIsWalletConnected()
  const referralProject = useSelector(selectReferralProject)

  const { isLoading, isError, data } = referralProject || {}
  const { pool, drop, referral, task } = data || {}

  const dispatch = useDispatch()

  const showModal = isError || (!isLoading && isNotNil(referralProject))

  const code = referral?.code
  useEffect(() => {
    if (!referral || !task) return
    dispatch(setReferralProjectData({ pool, drop, referral, task }))
    if (code) localStorage.setItem('referralCode', code)
  }, [code, dispatch, drop, pool, referral, task])

  const { push } = useHistory()
  const onErrorDismiss = useCallback(() => {
    dispatch(setReferralProject(undefined))
    localStorage.removeItem('referralCode')
    push('/')
  }, [dispatch, push])

  const closeInviteModal = useCallback(() => {
    dispatch(setReferralProject(undefined))
  }, [dispatch])

  const { initWeb3 } = useWeb3()
  const connectWallet = useCallback(() => {
    initWeb3()
    closeInviteModal()
  }, [closeInviteModal, initWeb3])

  return (
    <>
      <EmbeddableModal
        dismiss={isError || isConnected ? onErrorDismiss : closeInviteModal}
        isVisible={showModal}
      >
        <Box dc ac g={32} mw="500px">
          {isError ? (
            <InvalidReferral
              button={
                <ThemedButton onClick={onErrorDismiss} $styleType="primary">
                  Dismiss
                </ThemedButton>
              }
            />
          ) : isConnected ? (
            <AlreadyConnected
              button={
                <ThemedButton onClick={onErrorDismiss} $styleType="primary">
                  Dismiss
                </ThemedButton>
              }
            />
          ) : (
            <GetStarted
              button={
                <ThemedButton onClick={connectWallet} $styleType="primary">
                  Get started
                </ThemedButton>
              }
              referralVerification={task?.referralVerification}
            />
          )}
        </Box>
      </EmbeddableModal>
    </>
  )
}

function InvalidReferral({ button }: { button: ReactNode }) {
  return (
    <>
      <ModalTitle>Oops!</ModalTitle>
      <ModalText>
        <>
          It looks like the invite code you entered is invalid or expired.
          <br />
          <br />
          Please double-check your invite code and try again. If you continue to
          experience issues, don't hesitate to reach out for support.
        </>
      </ModalText>
      {button}
    </>
  )
}

function AlreadyConnected({ button }: { button: ReactNode }) {
  return (
    <>
      <ModalTitle>Hold up!</ModalTitle>
      <ModalText>
        <>
          You've already signed up to ZAP. You can't use a referral link after
          you've signed up.
          <br />
          <br />
          If you want to use this referral link with a new account, make sure to
          disconnect your wallet first.
        </>
      </ModalText>
      {button}
    </>
  )
}

function GetStarted({
  referralVerification,
  button,
}: {
  referralVerification: ReferralVerificationUnion
  button: ReactNode
}) {
  return (
    <>
      <ModalTitle>You've been invited to ZAP!</ModalTitle>
      <ModalText>
        {referralVerification === ReferralVerification.WALLET_CONNECT && (
          <>
            Click the button below to connect your wallet and check out the
            project.
          </>
        )}
        {referralVerification === ReferralVerification.COMPLETE_MISSION && (
          <>
            Click the button to connect your wallet and check out the project.
            <br />
            <br />
            Complete a mission to validate your referral!
          </>
        )}
        {referralVerification ===
          ReferralVerification.COMPLETE_MISSION_AND_EMAIL && (
          <>
            Click the button to connect your wallet and check out the project.
            <br />
            <br />
            Verify your email and complete a mission to validate your referral!
          </>
        )}
      </ModalText>
      {button}
    </>
  )
}

export default ReferralWelcomeModal
